import React, {useEffect, useState} from "react";
import {Button, Checkbox, ConfigProvider, Input, Modal, Select, Table} from "antd";
import {
    deleteTechnicianGroup,
    getTechnicianGroupListingWithTechnicians,
    updateTechnicianGroup,
} from "../../../Services/technicianGroup";
import {useDispatch} from "react-redux";
import {SET_IS_API_CALLED, SET_IS_API_CALLED_FINISHED,} from "../../../store/features/loadingSlice";
import {useDropdowns} from "../../../hooks/useDropdowns";
import TechnicianGroupManagementModal from "./TechnicianGroupManagementModal";
import {TechnicianManagementTablePageSize, TechnicianManagementTablePageSizeOptions} from "../../../constant";
import NotifyCreateRecordModal from "../../../components/Modals/NotifyCreateRecordModal";
import {arrayUtils} from "../../../utils/arrayUtils";
import {nanoid} from "nanoid";
import { clientColumnSorter } from "../../../utils/tableClientSort";

const TechnicianManagementPage = () => {
    const dispatch = useDispatch();
    const [editableRowKeys, setEditableRowKeys] = useState([]);
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [isActive, setIsActive] = useState(true);
    const [showNotifyCreateRecordModal, setShowNotifyCreateRecordModal] = useState(false);

    const [showModal, setShowModal] = useState(false);
    const [modalMessage, setModalMessage] = useState({});

    const [technicianGroupList, setTechnicianGroupList] = useState([]);
    const [technicianGroupPaginationdata, setTechnicianGroupPaginationData] =
        useState({});
    const {state, getTechnicianDropdown, getRegionsDropDown} = useDropdowns();
    const [showCreateTechnicianGroupModal, setShowCreateTechnicianGroupModal] =
        useState(false);
    const [showDeleteTechnicianGroupModal, setShowDeleteTechnicianGroupModal] =
        useState(false);
    const [tableParams, setTableParams] = useState({
        pagination: {
            current: 1,
            pageSize: TechnicianManagementTablePageSize,
            total: TechnicianManagementTablePageSize,
            showSizeChanger: true,
            pageSizeOptions: TechnicianManagementTablePageSizeOptions
        },
    });

    useEffect(() => {
        retrieveTechnicianGroupListing();

        getRegionsDropDown();
    }, []);

    const retrieveTechnicianGroupListing = (data) => {
        dispatch(SET_IS_API_CALLED("Retrieving Technician Data"));
        getTechnicianGroupListingWithTechnicians({
            pageSize: data?.pageSize ?? tableParams?.pagination.pageSize,
            pageIndex: data?.current ?? tableParams?.pagination.current,
        })
            .then((res) => {
                setTechnicianGroupList(res?.data?.items.map(item => {
                    // this is to add an immutable list containing existing technicians for future processing
                    return {
                        ...item,
                        key: nanoid(),
                        existingTechnicianIds: [...new Set(item.technicians.map(c => c.technicianId))], // set conversion is to remove duplicates
                        newTechnicianIds: [],
                        removedTechnicianIds: []
                    }
                }));
                setTechnicianGroupPaginationData(res?.data);
                setTableParams(
                    {
                        pagination: {
                            current: res?.data?.pageIndex,
                            pageSize: data?.pageSize ?? TechnicianManagementTablePageSize,
                            total: res?.data?.totalItems,
                            showSizeChanger: true,
                            pageSizeOptions: TechnicianManagementTablePageSizeOptions
                        }
                    });
            })
            .finally(() => {
                dispatch(SET_IS_API_CALLED_FINISHED());
            });
    };

    const onTechnicianListChange = (data, record) => {

        const editedRecords = technicianGroupList.map((technicianGroup) => {

            // get the original list of technicians  to be used as a reference
            const existingTechnicianList = record?.existingTechnicianIds ?? [];
            const normalizedData = data; //  the data that will be returned will be a list [1,2,3]

            if (technicianGroup.id === record.id) {
                // the data returned by the table is in array format, but need to explicitly declare it as an array to use the array methods
                // this is to retrieve the corresponding list of technicians from the technician dropdown and add it to the screen
                const sanitizedTechnicianData = state?.techniciansDropdown?.filter(t => [...data]?.some(item => t?.technicianId === (item)));

                // PART 2 HANDLING REMOVAL AND ADDING SIMULTANEOUSLY
                // need to check if the value added was part of the initial list (is it a new value?)
                const dataThatIsNotFromExistingList = normalizedData.filter(data => !existingTechnicianList?.includes(data));

                // PART 3 SYNCHRONIZING THE NEW TECHNICIAN LIST AND REMOVAL
                // check if data is in the removed list
                return resolveDifferencesBetweenTechnicianList("select", technicianGroup, sanitizedTechnicianData, normalizedData, dataThatIsNotFromExistingList);
            } else {
                return technicianGroup;
            }
        });
        setTechnicianGroupList(editedRecords);
    };

    const onTechnicianListDeselect = (data, record) => {
        const editedRecords = technicianGroupList.map((technicianGroup) => {

            // get the original list of technicians  to be used as a reference
            const existingTechnicianList = record?.existingTechnicianIds ?? [];
            const normalizedData = [data]; // the data returned will be a single data in int form: "1", so we standardize it to a list

            if (technicianGroup.id === record.id) {
                // need to show that the technician is removed from the UI
                const sanitizedTechnicianData = technicianGroup?.technicians?.filter(t => t?.technicianId !== data);

                // PART 2 HANDLING REMOVAL AND ADDING SIMULTANEOUSLY
                // need to check if the value removed was part of the initial list (is it an existing value?)

                const dataThatIsFromExistingList = normalizedData.filter(data => {
                    return existingTechnicianList?.includes(data);
                });

                const removedData = [...dataThatIsFromExistingList, ...technicianGroup?.removedTechnicianIds];


                // PART 3 SYNCHRONIZING THE NEW TECHNICIAN LIST AND REMOVAL
                // check if that one data is in the add list also
                return resolveDifferencesBetweenTechnicianList("deselect", technicianGroup, sanitizedTechnicianData, normalizedData, removedData);


            } else {
                return technicianGroup;
            }
        });
        setTechnicianGroupList(editedRecords);
    }

    // This function is used to synchronize the new technician list and removal technician list if there are common values between them
    const resolveDifferencesBetweenTechnicianList = (operationType, technicianGroup, sanitizedTechnicianData, normalizedData, technicianData) => {
        if (operationType === "select") {
            const dataFromRemovedTechnicianList = technicianGroup?.removedTechnicianIds?.filter(data => !normalizedData?.includes(data));

            return {
                ...technicianGroup,
                technicians: [...sanitizedTechnicianData], // for the display
                newTechnicianIds: technicianData,
                removedTechnicianIds: dataFromRemovedTechnicianList
            };
        } else {
            const dataFromNewTechnicianList = technicianGroup?.newTechnicianIds?.filter(data => !normalizedData?.includes(data));

            return {
                ...technicianGroup,
                technicians: sanitizedTechnicianData,
                removedTechnicianIds: technicianData,
                newTechnicianIds: dataFromNewTechnicianList,
            };
        }
    }

    const onRegionChange = (data, record) => {
        const sanitizedData = data !== undefined && data !== null ? data : null;
        const editedRecords = technicianGroupList.map((technician) => {
            if (technician.id === record.id) {
                return {
                    ...technician,
                    region: sanitizedData,
                };
            } else {
                return technician;
            }
        });
        setTechnicianGroupList(editedRecords);
        if (sanitizedData !== null) {
            retrieveTechnicianList(data);
        }
    };

    const onNameChange = (data) => {
        setName(data.target.value);
    };

    const onDescriptionChange = (data) => {
        setDescription(data.target.value);
    };

    const onIsActiveChange = (data) => {
        setIsActive(data.target.checked);
    }

    const setEditableRow = (record) => {
        if (editableRowKeys.length === 0) {
            setEditableRowKeys([record?.id]);

            if (record?.region !== null && record?.region !== undefined) {
                retrieveTechnicianList(record?.region);
            }

            setName(record?.name);
            setDescription(record?.description);
            setIsActive(record?.isActive);
        } else {
            setShowModal(true);
            setModalMessage({
                title: "Edit Error",
                message: "Only one record is allowed to be edited at a time",
            });
        }
    };

    const deleteTechnicianGroupRecord = (record) => {
        dispatch(SET_IS_API_CALLED("Removing Technician Group"));
        deleteTechnicianGroup([record?.id]).finally(() => {
            setShowDeleteTechnicianGroupModal(true);
            retrieveTechnicianGroupListing();
            dispatch(SET_IS_API_CALLED_FINISHED());
        });


    };

    const removeEditableRow = (record) => {
        // clear state for the next editable row
        const records = editableRowKeys.filter((id) => record?.id !== id);
        setEditableRowKeys(records);
        retrieveTechnicianGroupListing();
    };

    const saveRecord = (record) => {
        dispatch(SET_IS_API_CALLED("Updating Technician Group"));
        updateTechnicianGroup(record?.id, {
            id: record?.id,
            isActive: isActive,
            name: name,
            description: description,
            region: record?.region,
            // using set data structure here to remove duplicates if any
            newTechnicianIds: arrayUtils(record?.newTechnicianIds).removeDuplicatesInTheArray().returnValuesIfAny(),
            removedTechnicianIds: arrayUtils(record?.removedTechnicianIds).removeDuplicatesInTheArray().returnValuesIfAny(),
        }).finally(() => {
            dispatch(SET_IS_API_CALLED_FINISHED());
            removeEditableRow(record);
            retrieveTechnicianGroupListing();
        });
    };

    const retrieveTechnicianList = (regionData) => {
        dispatch(SET_IS_API_CALLED("Retrieving Technician Group"));
        getTechnicianDropdown().finally(() =>
            dispatch(SET_IS_API_CALLED_FINISHED())
        );
    };

    return (
        <>
            {showCreateTechnicianGroupModal && (
                <TechnicianGroupManagementModal
                    showCreateTechnicianGroupModal={showCreateTechnicianGroupModal}
                    setShowCreateTechnicianGroupModal={setShowCreateTechnicianGroupModal}
                    retrieveTechnicianGroupListing={retrieveTechnicianGroupListing}
                    showNotifyCreateRecordModal={showNotifyCreateRecordModal}
                    setShowNotifyCreateRecordModal={setShowNotifyCreateRecordModal}
                />
            )}

            {showNotifyCreateRecordModal && <NotifyCreateRecordModal
                modalTitle={"Technician Group Created"}
                modalText={`Your Technician Group has been created.`}
                setShowNotifyCreateRecordModal={setShowNotifyCreateRecordModal}
                showNotifyCreateRecordModal={showNotifyCreateRecordModal}
                timeout={1000}
                redirectUrl={undefined}
            />}

            {/* if there are no conditions here to prevent the rendering, it will throw a rendering error */}
            {showDeleteTechnicianGroupModal && <NotifyCreateRecordModal
                modalTitle={"Technician Group Deleted"}
                modalText={`Your Technician Group has been deleted.`}
                setShowNotifyCreateRecordModal={setShowDeleteTechnicianGroupModal}
                showNotifyCreateRecordModal={showDeleteTechnicianGroupModal}
                timeout={1000}
                redirectUrl={undefined}
            />}

            {showModal && (
                <Modal
                    title={modalMessage?.title}
                    open={showModal}
                    onOk={() => {
                        showModal(false);
                    }}
                    onCancel={() => {
                        setShowModal(false);
                    }}
                    footer={null}
                >
                    <p>{modalMessage?.message}</p>
                </Modal>
            )}

            <div className={"p-4"}>
                <div className={"flex flex-col gap-7"}>
                    <div>
                        <p className={"text-lg font-bold"}>Technician Group Management</p>
                    </div>
                    <ConfigProvider
                        theme={{
                            components: {
                                Button: {
                                    defaultBg: "#6479AC",
                                    defaultColor: "#FFFFFF",
                                    defaultHoverBg: "#FFFFFF",
                                    defaultHoverColor: "#6479AC",
                                    defaultHoverBorderColor: "#6479AC",
                                },
                                Table: {
                                    headerBg: "#F5F5F5",
                                    headerColor: "#2F5E8A",
                                    headerSortActiveBg: "#F5F5F5",
                                    headerSortHoverBg: "#F5F5F5",
                                    fixedHeaderSortActiveBg: "#F5F5F5",
                                },
                            },
                        }}
                    >
                        <div>
                            <Button
                                onClick={() => {
                                    setShowCreateTechnicianGroupModal(
                                        !showCreateTechnicianGroupModal
                                    );
                                }}
                                style={{
                                    marginBottom: 16,
                                }}
                            >
                                Add Technician Group
                            </Button>
                        </div>
                        <Table
                            dataSource={technicianGroupList ?? []}
                            columns={[
                                {
                                    title: "No",
                                    dataIndex: "id",
                                    width: "5%",
                                    sorter: clientColumnSorter("id"),
                                },
                                {
                                    title: "Group Name",
                                    dataIndex: "name",
                                    width: "15%",
                                    sorter: clientColumnSorter("name"),
                                    render: (text, record) => {
                                        const isEditing = editableRowKeys.includes(record?.id);

                                        return isEditing ? (
                                            <Input
                                                placeholder={"Please enter a name"}
                                                value={name}
                                                onChange={onNameChange}
                                            />
                                        ) : (
                                            text
                                        );
                                    },
                                },
                                {
                                    title: "Description",
                                    dataIndex: "description",
                                    width: "15%",
                                    sorter: clientColumnSorter("description"),
                                    render: (text, record) => {
                                        const isEditing = editableRowKeys.includes(record?.id);

                                        return isEditing ? (
                                            <Input
                                                placeholder={"Please enter a description"}
                                                value={description}
                                                onChange={onDescriptionChange}
                                            />
                                        ) : (
                                            text
                                        );
                                    },
                                },
                                {
                                    title: "Region",
                                    dataIndex: "region",
                                    width: "15%",
                                    sorter: clientColumnSorter("region"),
                                    render: (text, record) => {
                                        const isEditing = editableRowKeys.includes(record?.id);

                                        return isEditing ? (
                                            <Select
                                                style={{width: "100%"}}
                                                placeholder="Please Select A Region"
                                                onChange={(data) => {
                                                    onRegionChange(data, record);
                                                }}
                                                label={"name"}
                                                value={text}
                                                optionFilterProp="name"
                                                allowClear
                                                options={state.regionsDropDown?.map((region) => {
                                                    return {
                                                        value: region?.name,
                                                        label: region?.name,
                                                    };
                                                })}
                                            />
                                        ) : (
                                            text
                                        );
                                    },
                                },
                                {
                                    title: "Status",
                                    dataIndex: "isActive",
                                    width: "10%",
                                    render: (text, record) => {
                                        const isEditing = editableRowKeys.includes(record?.id);

                                        return (
                                            <Checkbox
                                                onChange={onIsActiveChange}
                                                disabled={!isEditing}
                                                defaultChecked={record?.isActive ?? false}
                                            ></Checkbox>
                                        );
                                    },
                                },
                                {
                                    title: "Technician List",
                                    dataIndex: "technicians",
                                    width: "25%",
                                    render: (text, record) => {
                                        const isEditing = editableRowKeys.includes(record?.id);

                                        return (
                                            <Select
                                                style={{width: "100%"}}
                                                onDeselect={(data) => {
                                                    onTechnicianListDeselect(data, record)
                                                }}
                                                placeholder="Please Select A Technician"
                                                disabled={!isEditing}
                                                onChange={(data) => {
                                                    onTechnicianListChange(data, record);
                                                }}
                                                filterOption={false}
                                                options={
                                                    !isEditing
                                                        ? record.technicians?.map((technician) => {
                                                            return {
                                                                value: technician?.technicianId,
                                                                label: technician?.name,
                                                            };
                                                        })
                                                        :
                                                        state.techniciansDropdown?.map((technician) => {
                                                            return {
                                                                value: technician?.technicianId,
                                                                label: technician?.name,
                                                            };
                                                        })
                                                }
                                                mode="multiple"
                                                // value={value}
                                                value={
                                                    // !isEditing
                                                    //     ?
                                                    text?.map((technician) => {
                                                        return {
                                                            value: technician?.technicianId,
                                                            label: technician?.name,
                                                        };
                                                    })
                                                }
                                                optionFilterProp="children"
                                            />
                                        );
                                    },
                                },
                                {
                                    title: "Action",
                                    key: "action",
                                    width: "25%",
                                    render: (text, record) => {
                                        const isEditing = editableRowKeys.includes(record?.id);
                                        return !isEditing ? (
                                            <div className={"flex gap-4"}>
                                                <Button
                                                    onClick={() => setEditableRow(record)}
                                                    className={"bg-dark-blue-100 text-white"}
                                                    // variant="primarygreen"
                                                >
                                                    Edit
                                                </Button>

                                                <Button
                                                    onClick={() => deleteTechnicianGroupRecord(record)}
                                                    className={"bg-dark-blue-100 text-white"}
                                                    // variant="primarygreen"
                                                >
                                                    Delete
                                                </Button>
                                            </div>
                                        ) : (
                                            <div className={"flex gap-4"}>
                                                <Button
                                                    onClick={() => saveRecord(record)}
                                                    className={"bg-dark-blue-100 text-white"}
                                                    // variant="primarygreen"
                                                >
                                                    Save
                                                </Button>

                                                <Button
                                                    onClick={() => removeEditableRow(record)}
                                                    className={"bg-dark-blue-100 text-white"}
                                                    // variant="primarygreen"
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                        );
                                    },
                                },
                            ]}
                            pagination={tableParams?.pagination}
                            onChange={retrieveTechnicianGroupListing}
                        />
                    </ConfigProvider>
                </div>
            </div>
        </>
    );
};

export default TechnicianManagementPage;
